import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useAuth } from '../auth/AuthContext';
import { ErrorBanner } from '../components/ErrorBanner';
import { useAppUi } from '../app/useAppUi';
import { PreferencesToolbar } from '../components/PreferencesToolbar';
import logoUrl from '../assets/logo.png';

// Unsplash (free licence), same photo family as the agent website.
const LOGIN_PHOTO = 'https://images.unsplash.com/photo-1759676015346-a2ea5f4a3caa?w=1400&q=70&auto=format&fit=crop';

export function LoginPage() {
  const { login, isAuthed } = useAuth();
  const { t } = useAppUi();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    if (isAuthed) navigate('/dashboard', { replace: true });
  }, [isAuthed, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login({ username, password });
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' }, bgcolor: 'background.paper' }}>
      {/* Photo side (desktop) */}
      <Box sx={{ position: 'relative', display: { xs: 'none', md: 'block' }, overflow: 'hidden' }}>
        <Box
          component="img"
          src={LOGIN_PHOTO}
          alt=""
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(2,6,23,0.85), rgba(2,6,23,0.25) 55%, rgba(2,6,23,0.1))' }} />
        <Stack sx={{ position: 'relative', height: '100%', p: 6, color: '#fff' }} justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box component="img" src={logoUrl} alt="" sx={{ width: 40, height: 40, borderRadius: 1.5 }} />
            <Typography sx={{ fontWeight: 700, fontSize: 20 }}>{t('layout.appName')}</Typography>
          </Stack>
          <Box sx={{ maxWidth: 440 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 40, lineHeight: 1.1, letterSpacing: '-0.02em' }}>
              {t('login.sideTitle')}
            </Typography>
            <Typography sx={{ mt: 2, fontSize: 16, color: 'rgba(255,255,255,0.8)' }}>{t('login.sideText')}</Typography>
          </Box>
        </Stack>
      </Box>

      {/* Form side */}
      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', px: { xs: 3, sm: 6 }, py: 8 }}>
        <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
          <PreferencesToolbar />
        </Box>
        <Box sx={{ width: '100%', maxWidth: 380 }}>
          <Box component="img" src={logoUrl} alt="" sx={{ width: 44, height: 44, borderRadius: 1.5, mb: 3, display: { md: 'none' } }} />
          <Typography variant="h5">{t('login.title')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3.5 }}>
            {t('login.subtitle')}
          </Typography>

          <ErrorBanner error={error} />

          <Box component="form" onSubmit={onSubmit}>
            <Stack spacing={2.25}>
              <TextField
                label={t('login.username')}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                required
                fullWidth
              />
              <TextField
                label={t('login.password')}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                fullWidth
              />
              <Button type="submit" variant="contained" size="large" disabled={loading} sx={{ height: 46 }}>
                {loading ? t('common.signingIn') : t('login.signIn')}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
